/**
 * The events grid's behavior half: reads the upcoming events page by page, keeps them by date, opens one. With the
 * mine option, marks each event a signed-in client has booked (web-studio-sites 6.6) and opens that booking. Draws
 * nothing.
 */
import { useMemo } from 'react';

import { useSiteEvents, useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone } from '../../format';
import { usePageMover } from '../../pageMover';
import { useEventMarks } from '../events-row/useEventMarks';
import { contract, type EventsGridActions, type EventsGridEvents, type EventsGridState } from './contract';

export type EventsGridRuntime = PieceRuntime<typeof contract, EventsGridState, EventsGridActions>;

export function useEventsGrid(input: PieceInput<EventsGridEvents> = {}): EventsGridRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const query = useSiteEvents();
  const studio = useSiteStudio();
  const timezone = useMemo(() => getClientTimezone(), []);

  const events = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap((p) => p.events)
        .sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime)),
    [query.data],
  );
  const marks = useEventMarks(events, options.mine, timezone);
  const status: EventsGridState['status'] = query.isError ? 'error' : query.isPending ? 'loading' : 'ready';
  const hasMore = Boolean(query.hasNextPage);
  const loadingMore = query.isFetchingNextPage;
  const { fetchNextPage } = query;

  const actions = useMemo<EventsGridActions>(
    () => ({
      open: (eventId) => {
        const event = events.find((e) => e.id === eventId);
        if (!event) return;
        emit('opened', { eventId });
        navigate(event.path);
      },
      loadMore: () => {
        if (hasMore && !loadingMore) void fetchNextPage();
      },
      home: () => navigate('/'),
      openBooking: (eventId) => {
        const mark = marks[eventId];
        if (mark) navigate(mark.action.href);
      },
    }),
    [events, marks, emit, navigate, hasMore, loadingMore, fetchNextPage],
  );

  return {
    state: { status, events, hasMore, loadingMore, studioName: studio.data?.name ?? null, timezone, marks },
    actions,
    options,
  };
}
